import { API_ENDPOINTS } from "config/apiConfig";
import { CompanyInfoType } from "common/types/companyInfo.type";
import { baseApi } from "./baseApi";

export type CompanyRadarRole = "seller" | "buyer";

export interface CompanyProfile {
  id: number;
  code: string;
  company_name: string;
  /** Path to the company logo on the backend (e.g. "/public/company-logos/x.png"). */
  logo_url?: string | null;
  email: string;
  postal_code: string;
  prefecture: string;
  address: string;
  building_room?: string;
  industry: string;
  contact_last_name: string;
  contact_first_name: string;
  phone_number: string;
  type: number;
  status: number;
}

export interface CompanyProfileResponse {
  data: CompanyProfile;
  message: string;
  success: boolean;
}

export interface UpdateCompanyLogoResponse {
  data: {
    logo_url: string;
  };
  message: string;
  success: boolean;
}

export type UpdateCompanyProfileRequest = CompanyInfoType & {
  status?: number;
  corp_regist_cert_url?: string | null;
};

export interface CompanyRadarMetrics {
  speed: number;
  reliability: number;
  stability: number;
  value: number | null;
  circulation: number;
}

export interface CompanyRadarMetricsResponse {
  data: {
    companyCode: string;
    role: CompanyRadarRole;
    period: {
      months: number;
      from: string;
      to: string;
    };
    metrics: CompanyRadarMetrics;
    calculatedAt: string;
  };
  message: string;
  success: boolean;
}

export interface CompanyCounter {
  companyCode: string;
  role: CompanyRadarRole;
  total_circulation_kg: number;
  stability: number;
  total_deals: number;
  today_connections: number;
  calculatedAt: string;
}

export interface SellerPublicCardBadge {
  id: string;
  code: string;
  name: string;
  tier: "gold" | "silver" | "bronze";
  description: string | null;
  icon_url: string;
  unlocked_at: string;
}

export interface CompanyBadge {
  id: string;
  code: string;
  name: string;
  tier: "gold" | "silver" | "bronze";
  description: string | null;
  icon_url: string;
  is_unlocked: boolean;
  unlocked_at: string | null;
  display_order: number | null;
}

export interface SellerPublicCard {
  company_id: string;
  company_code: string;
  company_name: string | null;
  logo_url: string | null;
  postal_code: string | null;
  address: string | null;
  total_deals: number;
  total_xp: number | null;
  current_level: number | null;
  metrics: {
    value: number | null;
    reliability: number | null;
    circulation: number | null;
  };
  badges: SellerPublicCardBadge[];
  calculated_at: string;
}

interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
}

export const companyProfileApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCompanyProfile: builder.query<
      CompanyProfileResponse,
      { companyCode: string }
    >({
      query: ({ companyCode }) => ({
        url: `${API_ENDPOINTS.COMPANY_INFO}/${companyCode}`,
      }),
      providesTags: (_result, _error, { companyCode }) => [
        { type: "Company", id: companyCode },
      ],
    }),
    getCompanyRadarMetrics: builder.query<
      CompanyRadarMetricsResponse,
      { companyCode: string }
    >({
      query: ({ companyCode }) => ({
        url: `${API_ENDPOINTS.COMPANY_INFO}/${companyCode}/radar-metrics`,
      }),
    }),
    getSellerPublicCard: builder.query<
      SellerPublicCard,
      { companyCode: string }
    >({
      query: ({ companyCode }) => ({
        url: `${API_ENDPOINTS.COMPANY_INFO}/${companyCode}/public-card`,
      }),
      transformResponse: (response: ApiResponse<SellerPublicCard>) =>
        response.data,
    }),
    getCompanyCounter: builder.query<CompanyCounter, { companyCode: string }>({
      query: ({ companyCode }) => ({
        url: `${API_ENDPOINTS.COMPANY_INFO}/${companyCode}/counter`,
      }),
      transformResponse: (response: ApiResponse<CompanyCounter>) =>
        response.data,
    }),
    getMyBadges: builder.query<CompanyBadge[], void>({
      query: () => ({
        url: "/gamification/me/badges",
      }),
      transformResponse: (response: ApiResponse<CompanyBadge[]>) =>
        response.data,
      providesTags: ["Badge"],
    }),
    getCompanyBadges: builder.query<CompanyBadge[], { companyId: string }>({
      query: ({ companyId }) => ({
        url: `/gamification/companies/${companyId}/badges`,
      }),
      transformResponse: (response: ApiResponse<CompanyBadge[]>) =>
        response.data,
      providesTags: ["Badge"],
    }),
    updateCompanyProfile: builder.mutation<
      CompanyProfileResponse,
      {
        companyCode: string;
        data: UpdateCompanyProfileRequest;
      }
    >({
      query: ({ companyCode, data }) => ({
        url: `${API_ENDPOINTS.COMPANY_INFO}/${companyCode}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { companyCode }) => [
        { type: "Company", id: companyCode },
        "Ranking",
      ],
    }),
    updateMyCompanyLogo: builder.mutation<
      UpdateCompanyLogoResponse,
      { companyCode: string; data: FormData }
    >({
      query: ({ data }) => ({
        url: `${API_ENDPOINTS.COMPANY_INFO}/me/logo`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { companyCode }) => [
        { type: "Company", id: companyCode },
        "Ranking",
      ],
    }),
  }),
});

export const {
  useGetCompanyProfileQuery,
  useGetCompanyRadarMetricsQuery,
  useGetCompanyCounterQuery,
  useGetMyBadgesQuery,
  useGetCompanyBadgesQuery,
  useGetSellerPublicCardQuery,
  useUpdateMyCompanyLogoMutation,
  useUpdateCompanyProfileMutation,
} = companyProfileApi;
